import type { ImageColumnProps } from '@modules/page-builder/types';
import ImageWithPaintPicker from '../../common/image-with-paint-picker/ImageWithPaintPicker';
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselPrevious,
    CarouselNext,
} from '@rocco/components/carousel';
import { ReactComponent as ArrowLeft } from '@rocco/icons/svg/arrow/arrow-chevron-left.svg';
import { ReactComponent as ArrowRight } from '@rocco/icons/svg/arrow/arrow-chevron-right.svg';
import { useState } from 'react';
import { HeartFill, HeartLine, Download2Line } from '@archipro-design/icons';
import { FollowSelector } from '~/modules/moodboards/components';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';
import { useTracker } from '@archipro-website/tracker';
import { encodeDownloadLink } from '../detail-single-image-shell/HoverButtonsImage';
import { useCanDownloadImages } from '@modules/user';

/**
 * Props for the FullPageGalleryModule component.
 */
interface FullPageGalleryModuleProps {
    /**
     * An array of image column properties to display in the gallery.
     */
    columns: ImageColumnProps[];
}

/**
 * FullPageGalleryModule is a component that displays a carousel of images.
 * It filters out columns without an imageUrl and shows navigation arrows and a slide counter.
 */
const FullPageGalleryModule = ({ columns }: FullPageGalleryModuleProps) => {
    const renderedColumns = columns.filter((column) => {
        return column.imageUrl;
    });
    const [currentSlide, setCurrentSlide] = useState(1);
    const { isInDesignBoard } = useHandleFollow();
    const tracker = useTracker();
    const canDownloadImages = useCanDownloadImages();

    /**
     * Determines if an image should be preloaded based on current slide position
     * Preloads current and next image for better user experience
     */
    const shouldPreloadImage = (index: number) => {
        const currentIndex = currentSlide - 1; // Convert to 0-based index
        const nextIndex = (currentIndex + 1) % renderedColumns.length;
        return index === currentIndex || index === nextIndex;
    };

    const currentSlideImage = renderedColumns[currentSlide - 1];

    return (
        <div className="mx-auto w-full md:max-w-455">
            <div className="w-full">
                <Carousel
                    onChangeSlide={({ currentIndex }) =>
                        setCurrentSlide(currentIndex + 1)
                    }
                >
                    <div className="aspect-1820/1014 h-fit overflow-hidden border md:rounded-2">
                        <CarouselContent>
                            {renderedColumns.map((column, index) => (
                                <CarouselItem key={column.id}>
                                    <div className="group relative md:w-455">
                                        <ImageWithPaintPicker
                                            width={
                                                column.dimension.width || 1820
                                            }
                                            height={
                                                column.dimension.height || 1014
                                            }
                                            src={column.imageUrl || ''}
                                            alt={
                                                column.caption ||
                                                `architectural plan image ${index + 1}`
                                            }
                                            className="aspect-1820/1014 md:max-w-455"
                                            loading={
                                                shouldPreloadImage(index)
                                                    ? 'eager'
                                                    : 'lazy'
                                            }
                                            rel={
                                                shouldPreloadImage(index)
                                                    ? 'preload'
                                                    : undefined
                                            }
                                            objectFit="cover"
                                        />
                                        <div className="absolute right-0 top-0 hidden items-center justify-between gap-2 p-8 text-white opacity-0 transition-opacity duration-500 ease-in-out group-hover:opacity-100 md:flex">
                                            <FollowSelector
                                                trigger={
                                                    <button
                                                        aria-label="Follow"
                                                        className="before:backdrop-hack bg-gray-100/50 flex items-center justify-center rounded-full p-3 backdrop-blur-xl hover:bg-gray-100"
                                                    >
                                                        {isInDesignBoard(
                                                            column.libraryImage
                                                                ?.ID ?? 0,
                                                            'PhotoPin',
                                                        ) ? (
                                                            <HeartFill className="size-6" />
                                                        ) : (
                                                            <HeartLine className="size-6" />
                                                        )}
                                                    </button>
                                                }
                                                pinId={
                                                    column.libraryImage?.ID ?? 0
                                                }
                                                libraryItemId={
                                                    column.libraryImage
                                                        ?.LibraryItemID ?? 0
                                                }
                                                pinType="PhotoPin"
                                            />
                                            {canDownloadImages &&
                                                column.libraryImage
                                                    ?.DownloadLink && (
                                                    <a
                                                        className="bg-gray-100/50 flex items-center justify-center rounded-full p-3 backdrop-blur-xl hover:bg-gray-100"
                                                        href={encodeDownloadLink(
                                                            column.libraryImage
                                                                ?.DownloadLink ||
                                                                '',
                                                        )}
                                                        download
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            tracker.log(
                                                                'StaffDownloadImage',
                                                                {
                                                                    url: new URL(
                                                                        window
                                                                            .location
                                                                            .href,
                                                                    ),
                                                                    data: {
                                                                        ExtraData:
                                                                            column
                                                                                .libraryImage
                                                                                ?.ID ??
                                                                            0,
                                                                    },
                                                                },
                                                            );
                                                        }}
                                                        aria-label="Download"
                                                    >
                                                        <Download2Line className="size-6" />
                                                    </a>
                                                )}
                                        </div>
                                    </div>
                                </CarouselItem>
                            ))}
                        </CarouselContent>

                        <div className="absolute top-1/2 hidden h-20 w-full -translate-y-1/2 items-center justify-between px-7 md:flex">
                            <CarouselPrevious
                                size="square42"
                                aria-label="Previous"
                                className="border-0 bg-[rgba(68,68,68,1)] text-white backdrop-blur-[15px] hover:bg-[rgba(68,68,68,0.8)] focus:ring-0"
                            >
                                <ArrowLeft className="size-5 fill-white text-white" />
                            </CarouselPrevious>
                            <CarouselNext
                                size="square42"
                                aria-label="Next"
                                className="border-0 bg-[rgba(68,68,68,1)] text-white backdrop-blur-[15px] hover:bg-[rgba(68,68,68,0.8)] focus:ring-0"
                            >
                                <ArrowRight className="size-5 fill-white text-white" />
                            </CarouselNext>
                        </div>
                        {renderedColumns.length > 1 && (
                            <div className="rounded-xs absolute bottom-2 right-3 flex scale-75 items-center justify-between bg-black/70 px-2 py-0.5 text-sm text-white md:scale-100">
                                {currentSlide} / {renderedColumns.length}
                            </div>
                        )}
                    </div>
                </Carousel>
            </div>
            {currentSlideImage?.caption && (
                <div className="md:mb-31.25 px-4.5 pt-3 font-display text-3 font-normal leading-[120%] tracking-[0.015em] text-[#161616] md:ml-0 md:w-325 md:px-0 md:pt-12 md:text-5.5 md:font-normal md:leading-[120%]">
                    {currentSlideImage.caption || ''}
                </div>
            )}
        </div>
    );
};

export default FullPageGalleryModule;
